import styled from '@emotion/styled';
import { Fragment } from 'react';
import { fromUnixTime, getDate } from 'date-fns';
import { IEnrichedFlight } from '../../../shared/flight/domain/models/IEnrichedFlight';
import { FlightListRow } from './FlightListRow';
import { FlightListHeader } from './FlightListHeader';
import { FlightListScope } from '../../domain/constants/FlightListScope';
import { Color } from '../../../../../styles/Color';
import { getFlightDayAndDate } from '../../../detail/ui/helpers/getFlightDayAndDate';
import { convertDateToTimeZone } from '../../../../../utils/convertDateToTimeZone';
import { Css, ITheme, Responsive, RowRole, TableRole } from '../../../../../ui';
import { getFlightTimeForSorting } from '../../domain/getFlightTimeForSorting';

export const StyledFlightList = styled(TableRole)`
	display: flex;
	flex-direction: column;
	margin: ${Css.pxToRem(32)} 0;
	gap: ${Css.pxToRem(16)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		gap: 0;
		margin-top: ${Css.pxToRem(16)};
	}
`;

export const FlightListDateRow = styled.fieldset`
	border: none;
	border-top: 1px solid ${Color.BorderCard};

	padding: 0;
	text-align: center;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(32)};
		margin-bottom: ${Css.pxToRem(32)};
	}
`;

export const StyledRow = styled(RowRole)<{}, ITheme>`
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	padding: ${Css.pxToRem(20)} 0;
	border-bottom: 1px solid ${Color.Neutral2};
	max-height: ${Css.pxToRem(80)};

	:has(+ ${FlightListDateRow}) {
		border-bottom: none;
	}

	:last-of-type {
		border-bottom: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
		min-height: ${Css.pxToRem(96)};
		padding: ${Css.pxToRem(16)} 0;
	}
`;

interface IProps {
	flights: IEnrichedFlight[];
	scope: FlightListScope;
}

export function FlightList({ flights, scope }: IProps) {
	return (
		<StyledFlightList>
			<FlightListHeader scope={scope} />

			{flights.map((flight, index) => {
				const previousFlight = flights[index - 1];

				const previousFlightDateTime = previousFlight
					? convertDateToTimeZone(
							fromUnixTime(getFlightTimeForSorting(previousFlight)),
					  )
					: undefined;

				const previousFlightDate = previousFlightDateTime
					? getDate(previousFlightDateTime)
					: undefined;

				const flightDateTime = convertDateToTimeZone(
					fromUnixTime(getFlightTimeForSorting(flight)),
				);

				const flightDate = getDate(flightDateTime);

				return (
					<Fragment key={flight.id}>
						{previousFlightDate && flightDate !== previousFlightDate && (
							<FlightListDateRow>
								<legend>
									<StyledDate>{getFlightDayAndDate(flightDateTime)}</StyledDate>
								</legend>
							</FlightListDateRow>
						)}
						<FlightListRow flight={flight} scope={scope} />
					</Fragment>
				);
			})}
		</StyledFlightList>
	);
}

const StyledDate = styled.h5`
	margin-bottom: 0;
	padding-left: ${Css.pxToRem(16)};
	padding-right: ${Css.pxToRem(16)};
`;
